import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, FileType2, Trash2, Loader2, FolderOpen, Download } from "lucide-react";
import { toast } from "sonner";

import Header from "../components/Header";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import api from "../lib/api";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function MySpecificationsPage() {
  const nav = useNavigate();
  const [specs, setSpecs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [branded, setBranded] = useState(true);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchSpecs = async () => {
    setLoading(true);
    try {
      const r = await api.get("/spec/mine");
      setSpecs(r.data);
    } catch (e) {
      toast.error("Could not load specifications", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSpecs();
  }, []);

  const redownload = async (spec, fmt) => {
    setBusyId(`${spec.id}-${fmt}`);
    try {
      const res = await api.post(
        "/spec/mine/redownload",
        { spec_id: spec.id, format: fmt, branded },
        { responseType: "blob" }
      );
      const mime =
        fmt === "pdf"
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const blob = new Blob([res.data], { type: mime });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safe = (spec.project_name || "specification").replace(/[^\w-]+/g, "_");
      const suffix = branded ? "_Helvar" : "";
      a.download = `Specification_${safe}${suffix}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${fmt.toUpperCase()}`, {
        description: spec.project_name,
      });
    } catch (e) {
      toast.error("Download failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    const id = pendingDelete.id;
    setBusyId(`${id}-delete`);
    try {
      await api.delete(`/spec/mine/${id}`);
      setSpecs((prev) => prev.filter((s) => s.id !== id));
      toast.success("Specification deleted");
    } catch (e) {
      toast.error("Delete failed", {
        description: e?.response?.data?.detail || e.message,
      });
    } finally {
      setBusyId(null);
      setPendingDelete(null);
    }
  };

  return (
    <div data-testid="my-specifications-page" className="min-h-screen flex flex-col bg-white">
      <Header />

      <div className="max-w-6xl w-full mx-auto px-6 lg:px-10 pt-12 pb-16">
        <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[#c80f3f]">
          Your Account
        </div>
        <div className="mt-2 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1f2a44] tracking-tight">
            My Specifications
          </h1>
          <div className="flex items-center gap-3 border border-[#e5e7eb] rounded-sm px-4 py-2">
            <Switch
              id="my-specs-branded-toggle"
              checked={branded}
              onCheckedChange={setBranded}
              data-testid="my-specs-branded-toggle"
              className="data-[state=checked]:bg-[#c80f3f]"
            />
            <Label
              htmlFor="my-specs-branded-toggle"
              className="text-xs text-[#1f2937] font-medium cursor-pointer select-none"
            >
              {branded ? "Helvar-branded downloads" : "Unbranded downloads"}
            </Label>
          </div>
        </div>
        <p className="mt-3 text-sm text-[#4b5563] max-w-2xl leading-relaxed">
          Every specification you generate is saved here. Re-download an existing
          project as PDF or Word, or remove it from your history.
        </p>

        <div className="mt-8">
          {loading && (
            <div className="text-sm text-[#4b5563] py-16 text-center">
              <Loader2 className="h-5 w-5 animate-spin mx-auto mb-3" strokeWidth={1.75} />
              Loading your specifications…
            </div>
          )}
          {!loading && specs && specs.length === 0 && (
            <Card
              data-testid="my-specs-empty"
              className="border border-dashed border-[#e5e7eb] rounded-sm shadow-none"
            >
              <div className="p-10 text-center">
                <FolderOpen className="h-8 w-8 mx-auto text-[#4b5563]" strokeWidth={1.5} />
                <div className="mt-3 font-display text-lg font-semibold text-[#1f2a44]">
                  No specifications yet
                </div>
                <p className="mt-1 text-sm text-[#4b5563]">
                  Build your first project-specific specification to see it here.
                </p>
                <Button
                  onClick={() => nav("/wizard")}
                  data-testid="my-specs-start-builder"
                  className="mt-6 bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm px-6"
                >
                  Start Specification Builder
                </Button>
              </div>
            </Card>
          )}
          {!loading && specs && specs.length > 0 && (
            <div className="space-y-3">
              {specs.map((spec) => (
                <SpecRow
                  key={spec.id}
                  spec={spec}
                  busyId={busyId}
                  onPdf={() => redownload(spec, "pdf")}
                  onDocx={() => redownload(spec, "docx")}
                  onDelete={() => setPendingDelete(spec)}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <AlertDialog
        open={!!pendingDelete}
        onOpenChange={(o) => !o && setPendingDelete(null)}
      >
        <AlertDialogContent
          data-testid="delete-confirm-dialog"
          className="rounded-sm bg-white border-[#e5e7eb]"
        >
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-xl text-[#1f2a44]">
              Delete this specification?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#4b5563]">
              This will permanently remove{" "}
              <span className="font-semibold text-[#1f2937]">
                {pendingDelete?.project_name || "this specification"}
              </span>{" "}
              from your history. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              data-testid="delete-cancel-button"
              className="rounded-sm"
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              data-testid="delete-confirm-button"
              className="bg-[#c80f3f] hover:bg-[#a10c32] text-white rounded-sm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function SpecRow({ spec, busyId, onPdf, onDocx, onDelete }) {
  const pdfBusy = busyId === `${spec.id}-pdf`;
  const docxBusy = busyId === `${spec.id}-docx`;
  const delBusy = busyId === `${spec.id}-delete`;
  return (
    <Card
      data-testid={`spec-row-${spec.id}`}
      className="border border-[#e5e7eb] rounded-sm shadow-none"
    >
      <div className="p-5 sm:p-6 flex flex-col lg:flex-row lg:items-center gap-5">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.22em] font-semibold text-[#4b5563]">
            <span className="text-[#c80f3f]">{spec.segment}</span>
            <span>·</span>
            <span className="font-mono">{spec.clause_count} clauses</span>
            <span>·</span>
            <span>{formatDate(spec.created_at)}</span>
          </div>
          <div
            data-testid={`spec-name-${spec.id}`}
            className="mt-1 font-display text-lg sm:text-xl font-semibold text-[#1f2a44] truncate"
          >
            {spec.project_name || "Untitled specification"}
          </div>
        </div>

        <div className="flex items-center flex-wrap gap-2">
          <Button
            variant="outline"
            onClick={onPdf}
            disabled={pdfBusy || docxBusy || delBusy}
            data-testid={`redownload-pdf-${spec.id}`}
            className="rounded-sm border-[#e5e7eb] text-[#1f2a44] hover:border-[#1f2a44]"
          >
            {pdfBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <FileText className="mr-2 h-4 w-4" strokeWidth={1.75} />
            )}
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={onDocx}
            disabled={pdfBusy || docxBusy || delBusy}
            data-testid={`redownload-docx-${spec.id}`}
            className="rounded-sm border-[#e5e7eb] text-[#1f2a44] hover:border-[#1f2a44]"
          >
            {docxBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.75} />
            ) : (
              <FileType2 className="mr-2 h-4 w-4" strokeWidth={1.75} />
            )}
            Word
          </Button>
          <Button
            variant="ghost"
            onClick={onDelete}
            disabled={pdfBusy || docxBusy || delBusy}
            data-testid={`delete-spec-${spec.id}`}
            className="rounded-sm text-[#4b5563] hover:text-[#c80f3f] hover:bg-[#c80f3f]/5"
          >
            <Trash2 className="h-4 w-4" strokeWidth={1.75} />
          </Button>
        </div>
      </div>
    </Card>
  );
}
